import { useCallback, type KeyboardEvent } from 'react';

/**
 * Options for useArrowNavigation hook
 */
export interface UseArrowNavigationOptions {
    /** Total number of items in the list */
    itemCount: number;
    /** Whether navigation is disabled */
    disabled?: boolean;
    /** Array of refs to the navigable elements */
    refs: React.MutableRefObject<(HTMLElement | null)[]>;
    /** Callback to execute when an item is navigated to */
    onNavigate?: (index: number) => void;
    /** Optional callback to execute when an item is selected (e.g., on Enter/Space) */
    onSelect?: (index: number) => void;
}

/**
 * Hook for handling arrow key navigation in lists
 *
 * Provides keyboard navigation for lists using arrow keys, Home, and End keys.
 * Supports both horizontal (ArrowLeft/ArrowRight) and vertical (ArrowUp/ArrowDown) navigation.
 *
 * @example
 * ```tsx
 * const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
 * const handleKeyDown = useArrowNavigation({
 *   itemCount: items.length,
 *   disabled: false,
 *   refs: buttonRefs,
 *   onNavigate: (index) => {
 *     // Focus and optionally select the item at index
 *   },
 * });
 *
 * <button onKeyDown={(e) => handleKeyDown(e, 0)}>Item 1</button>
 * ```
 */
export const useArrowNavigation = ({
    itemCount,
    disabled = false,
    refs,
    onNavigate,
    onSelect,
}: UseArrowNavigationOptions) => {
    return useCallback(
        (event: KeyboardEvent<HTMLElement>, currentIndex: number) => {
            if (disabled || itemCount === 0) {
                return;
            }

            let targetIndex = currentIndex;

            switch (event.key) {
                case 'ArrowLeft':
                case 'ArrowUp':
                    event.preventDefault();
                    targetIndex = currentIndex > 0 ? currentIndex - 1 : itemCount - 1;
                    break;
                case 'ArrowRight':
                case 'ArrowDown':
                    event.preventDefault();
                    targetIndex = currentIndex < itemCount - 1 ? currentIndex + 1 : 0;
                    break;
                case 'Home':
                    event.preventDefault();
                    targetIndex = 0;
                    break;
                case 'End':
                    event.preventDefault();
                    targetIndex = itemCount - 1;
                    break;
                case 'Enter':
                case ' ':
                    if (onSelect) {
                        event.preventDefault();
                        onSelect(currentIndex);
                    }
                    return;
                default:
                    return;
            }

            const targetElement = refs.current[targetIndex];
            if (targetElement) {
                targetElement.focus();
                if (onNavigate) {
                    onNavigate(targetIndex);
                }
            }
        },
        [disabled, itemCount, refs, onNavigate, onSelect],
    );
};
